import * as RadixContextMenu from "@radix-ui/react-context-menu";
import classNames from "classnames";
import React, { ForwardedRef, forwardRef } from "react";

export const ContextMenu = forwardRef(
  (
    {
      children,
      onMouseLeave,
      verticalPadding = true,
      className,
    }: {
      children: React.ReactNode;
      onMouseLeave?: (e: React.MouseEvent) => void;
      verticalPadding?: boolean;
      className?: string;
    },
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    return (
      <div
        onMouseLeave={onMouseLeave}
        className={classNames(
          "bg-neutral-0 rounded-md shadow-lg flex flex-col overflow-hidden min-w-36 popover-scale-in",
          { "py-1.5": verticalPadding },
          className
        )}
        ref={ref}
      >
        {children}
      </div>
    );
  }
);

// TODO: merge this with DropdownMenuItem
export const ContextMenuItem = forwardRef<
  HTMLButtonElement | null,
  {
    children: React.ReactNode;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
    onClick: () => void;
    color?: "red" | "light-red" | "custom";
    className?: string;
  }
>(({ children, onClick, color, className, ...props }, ref) => {
  return (
    <button
      {...props}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.focus();
      }}
      className={classNames(
        "w-full h-8 flex font-medium items-center justify-between text-left px-3",
        color === "red"
          ? "text-red-500 hover:bg-red-500 hover:text-neutral-0"
          : color === "light-red"
          ? "text-red-400 hover:bg-red-500 hover:text-neutral-0"
          : color !== "custom"
          ? "text-neutral-600 dark:focus:text-neutral-700 hover:bg-neutral-100 active:bg-neutral-150"
          : "",
        className
      )}
      ref={ref}
    >
      {children}
    </button>
  );
});

export const RadixContextMenuContent = forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    onCloseAutoFocus?: React.ComponentProps<
      typeof RadixContextMenu.Content
    >["onCloseAutoFocus"];
    className?: string;
  }
>(({ children, onCloseAutoFocus, className }, forwardedRef) => {
  return (
    <RadixContextMenu.Content
      onCloseAutoFocus={onCloseAutoFocus}
      className={classNames(
        "bg-neutral-0 py-1.5 rounded-md shadow-lg flex flex-col overflow-hidden min-w-36 popover-scale-in",
        className
      )}
      ref={forwardedRef}
    >
      {children}
    </RadixContextMenu.Content>
  );
});

export const RadixContextMenuItem = ({
  children,
  color,
  className,
  ...props
}: {
  children: React.ReactNode;
  color?: "red" | "light-red" | "custom";
  className?: string;
} & React.ComponentPropsWithoutRef<typeof RadixContextMenu.Item>) => {
  return (
    <RadixContextMenu.Item
      {...props}
      className={classNames(
        "w-full h-8 flex font-medium items-center justify-between text-left px-3 select-none no-focus-visible",
        color === "red"
          ? "text-red-500 focus:bg-red-500 focus:text-neutral-0"
          : color === "light-red"
          ? "text-red-400 focus:bg-red-500 focus:text-neutral-0"
          : color !== "custom"
          ? "text-neutral-600 focus:bg-neutral-100"
          : "",
        { "text-opacity-50": props.disabled === true },
        className
      )}
    >
      {children}
    </RadixContextMenu.Item>
  );
};
